import { create } from 'zustand';
import { API_ENDPOINTS } from '@/config/api.config.ts';
import type { MeResponse } from './adminTypes';
import { PERM_TTL_MS } from './adminTypes';
import { hasPerm, type RequiredPerms } from './permissions';
import { adminClient } from '../api/adminClient';

interface PermState {
  permissions: string[];
  fetchedAt: number | null;
  isFetching: boolean;
  /** Store permissions and stamp fetch time. */
  setPerms: (permissions: string[]) => void;
  /** True when never fetched or older than `ttl` (default 60s). */
  isStale: (ttl?: number) => boolean;
  /** Stale-while-revalidate check: returns current value, revalidates in background. */
  has: (key: string | RequiredPerms) => boolean;
  /** Clear all permissions (logout). */
  clearPerms: () => void;
  /** Fetch `GET /api/admin/staff/me/permissions`. Never clears on error. */
  fetchPermissions: () => Promise<void>;
}

export const usePermStore = create<PermState>()((set, get) => ({
  permissions: [],
  fetchedAt: null,
  isFetching: false,

  setPerms: (permissions) => set({ permissions, fetchedAt: Date.now() }),

  isStale: (ttl = PERM_TTL_MS) => {
    const { fetchedAt } = get();
    if (fetchedAt === null) return true;
    return Date.now() - fetchedAt > ttl;
  },

  has: (key) => {
    const { permissions, isStale, fetchPermissions } = get();
    if (isStale()) {
      // Fire-and-forget revalidation; never blocks render.
      void fetchPermissions().catch(() => undefined);
    }
    return hasPerm(permissions, key);
  },

  clearPerms: () => set({ permissions: [], fetchedAt: null }),

  fetchPermissions: async () => {
    if (get().isFetching) return;
    set({ isFetching: true });
    try {
      const me = await adminClient.get<MeResponse>(API_ENDPOINTS.admin.mePermissions);
      if (me.data?.permissions) {
        set({ permissions: me.data.permissions, fetchedAt: Date.now() });
      }
    } catch {
      // Any error (incl. 404/501 backend pending): keep existing, do not clear.
    } finally {
      set({ isFetching: false });
    }
  },
}));
